import {
    globalLoggerToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
} from "@/tokens";
import GrowaveConfigurationEntity from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationEntity";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import GwStorefrontAppInfo from "@interfaces/GwStorefrontAppInfo";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { ModuleInterface } from "@interfaces/ModuleInterface";

import ApplyDiscountAvailabilityProvider from "./ApplyDiscountAvailabilityProvider/ApplyDiscountAvailabilityProvider";
import { BaseUrlProvider } from "./BaseUrlProvider/BaseUrlProvider";
import CartAvailabilityProvider from "./CartAvailabilityProvider/CartAvailabilityProvider";
import { DesignModeProviderInterface } from "./DesignModeProvider/DesignModeProviderInterface";
import { FromShopifyObjectDesignModeProvider } from "./DesignModeProvider/FromShopifyObjectDesignModeProvider";
import DesignModeQueryParamsProvider from "./DesignModeQueryParamsProvider/DesignModeQueryParamsProvider";
import DesignModeQueryParamsProviderInterface from "./DesignModeQueryParamsProvider/DesignModeQueryParamsProviderInterface";
import {
    applyDiscountAvailabilityProviderToken,
    cartAvailabilityProviderToken,
    pathPrefixProviderToken,
    referrerProviderToken,
} from "./di_tokens";
import { GraphQLApiClient } from "./GraphQLApiClient/GraphQLApiClient";
import HashParamsProvider from "./HashParamsProvider/HashParamsProvider";
import LocationController from "./LocationController/LocationController";
import LocationControllerInterface from "./LocationController/LocationControllerInterface";
import { PathPrefixProvider } from "./PathPrefixProvider/PathPrefixProvider";
import ReferrerProvider from "./ReferrerProvider/ReferrerProvider";
import ReferrerProviderInterface from "./ReferrerProvider/ReferrerProviderInterface";
import { ShopifyUrlUtils } from "./ShopifyUrlUtils/ShopifyUrlUtils";
import { StorefrontApiBaseUrlProvider } from "./StorefrontApiBaseUrlProvider/StorefrontApiBaseUrlProvider";
import { URLsBuilder } from "./URLsBuilder/URLsBuilder";

interface CommonModuleExports {
    locationController: LocationControllerInterface;
    referrerProvider: ReferrerProviderInterface;
    applyDiscountAvailabilityProvider: ApplyDiscountAvailabilityProvider;
    cartAvailabilityProvider: CartAvailabilityProvider;
    designModeProvider: DesignModeProviderInterface;
    designModeQueryParamsProvider: DesignModeQueryParamsProviderInterface;
    baseUrlProvider: BaseUrlProvider;
    graphqlApiClient: GraphQLApiClient | null;
    urlsBuilder: URLsBuilder;
    pathPrefixProvider: PathPrefixProvider;
    hashParamsProvider: HashParamsProvider;
}

@singleton()
export class CommonModule implements ModuleInterface<CommonModuleExports> {
    public exports: CommonModuleExports;

    public constructor(
        @inject(globalLoggerToken)
        private readonly globalLogger: LoggerInterface,
        @inject(gwStorefrontAppInfoToken)
        private readonly gwStorefrontAppInfo: GwStorefrontAppInfo,
        @inject(growaveConfigurationToken)
        private readonly growaveConfiguration: GrowaveConfigurationEntity
    ) {
        const logger = this.globalLogger.newLogger("CommonModule");
        logger.debug("CommonModule is initialized");

        const localContainer = container.createChildContainer();

        localContainer.register(applyDiscountAvailabilityProviderToken, {
            useFactory: instanceCachingFactory(() => {
                return new ApplyDiscountAvailabilityProvider(
                    growaveConfiguration.isDiscountApplyAvailable
                );
            }),
        });

        localContainer.register(cartAvailabilityProviderToken, {
            useFactory: instanceCachingFactory(() => {
                return new CartAvailabilityProvider(
                    growaveConfiguration.isCartAvailable
                );
            }),
        });

        localContainer.register(pathPrefixProviderToken, {
            useFactory: instanceCachingFactory(() => {
                return new PathPrefixProvider(
                    window.Shopify?.routes?.root || "/"
                );
            }),
        });

        localContainer.register(referrerProviderToken, {
            useFactory: instanceCachingFactory(() => {
                return new ReferrerProvider();
            }),
        });

        const locationController = new LocationController(window.location);

        const designModeProvider = new FromShopifyObjectDesignModeProvider();

        const designModeQueryParamsProvider = new DesignModeQueryParamsProvider(
            designModeProvider,
            locationController
        );

        const baseUrlProvider = new BaseUrlProvider(
            this.gwStorefrontAppInfo,
            this.growaveConfiguration
        );

        const storefrontApiBaseUrlProvider = new StorefrontApiBaseUrlProvider(
            this.gwStorefrontAppInfo,
            this.growaveConfiguration
        );

        let graphqlApiClient: GraphQLApiClient | null = null;
        if (growaveConfiguration.storefrontApiAccessToken) {
            graphqlApiClient = new GraphQLApiClient(
                storefrontApiBaseUrlProvider.appendToBaseUrl(
                    "/api/2024-07/graphql.json"
                ),
                growaveConfiguration.storefrontApiAccessToken
            );
        }

        const shopifyUrlUtils = new ShopifyUrlUtils(
            window.Shopify?.routes?.root || "/"
        );

        const urlsBuilder = new URLsBuilder(
            shopifyUrlUtils,
            locationController
        );

        const hashParamsProvider = new HashParamsProvider(locationController);

        this.exports = {
            get referrerProvider() {
                return localContainer.resolve(referrerProviderToken);
            },
            get applyDiscountAvailabilityProvider() {
                return localContainer.resolve(
                    applyDiscountAvailabilityProviderToken
                );
            },
            get cartAvailabilityProvider() {
                return localContainer.resolve(cartAvailabilityProviderToken);
            },
            get pathPrefixProvider() {
                return localContainer.resolve(pathPrefixProviderToken);
            },
            locationController,
            designModeProvider,
            designModeQueryParamsProvider,
            baseUrlProvider,
            graphqlApiClient,
            urlsBuilder,
            hashParamsProvider,
        };
    }

    public registerProviders() {
        return {};
    }
}
